import { useContext, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { TailSpin } from 'react-loader-spinner';
import { useParams } from 'react-router-dom';

import { BudgetFabric } from 'types/api/budgets.type';

import { useAppSelector } from 'redux/hooks';

import { budgetDataService } from 'services/budget.service';

import { BudgetContext } from 'contexts';

import { useFetch } from 'hooks';

import { checkParams } from 'helpers/checkParams';

import { BudgetListSkeleton } from '../BudgetListSkeleton';
import * as S from './Fabric.styles';
import { Item } from './components/Item';

type FabricProps = {
  /** False while the tab is kept mounted but hidden: no infinite-scroll loading then. */
  isActive?: boolean;
};

export function Fabric({ isActive = true }: FabricProps): React.ReactElement {
  const { id, gender } = useParams();

  const { season, zone, marketLine, warehouse } = useAppSelector((state) => state.settings);
  const { params: contextParams } = useContext(BudgetContext);

  const [displayedItems, setDisplayedItems] = useState<BudgetFabric[]>([]);
  const [allFabrics, setAllFabrics] = useState<BudgetFabric[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const params = {
    season,
    zone,
    seasonLine: marketLine,
    warehouse: warehouse.code,
    budgetNum: id!,
    genderCode: gender!,
    type: contextParams?.type || '',
  };

  const { content, isLoading } = useFetch(
    checkParams(params) ? () => budgetDataService.getFabrics(params) : undefined,
    [season, zone, marketLine, warehouse, id, gender, contextParams?.type]
  );

  const ITEMS_PER_PAGE = 20;
  const hasMore = currentIndex < allFabrics.length;

  const loadMore = () => {
    setCurrentIndex((prevIndex) => {
      const nextIndex = prevIndex + ITEMS_PER_PAGE;
      const newItems = allFabrics.slice(prevIndex, nextIndex);
      setDisplayedItems((prev) => [...prev, ...newItems]);
      return nextIndex;
    });
  };

  const dataMapped = displayedItems?.map((item: BudgetFabric) => (
    <Item key={item?.fabric} data={item} />
  ));

  useEffect(() => {
    if (content) {
      setAllFabrics(content);
      setDisplayedItems([]);
      setCurrentIndex(0);
    }
  }, [content]);

  useEffect(() => {
    if (allFabrics.length > 0 && displayedItems.length === 0 && currentIndex === 0) {
      const initialItems = allFabrics.slice(0, ITEMS_PER_PAGE);
      setDisplayedItems(initialItems);
      setCurrentIndex(ITEMS_PER_PAGE);
    }
  }, [allFabrics, displayedItems.length, currentIndex]);

  if (isLoading) {
    return <BudgetListSkeleton withImage={false} columns={5} />;
  }

  return (
    <InfiniteScroll
      hasMore={hasMore && isActive}
      loadMore={loadMore}
      loader={
        <S.LoadingContainer key="loader">
          <TailSpin width="100" color="#286090" />
        </S.LoadingContainer>
      }
    >
      {dataMapped}
    </InfiniteScroll>
  );
}
